import { Body1, Spinner, Title2 } from '@fluentui/react-components';
import { useCallback, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router';

import { useWorkspaces } from '../../../hooks/useWorkspaces/useWorkspaces.js';
import {
  DEFAULT_GRADER_MODEL,
  GRADER_MODEL_VARIABLE,
} from '../../../services/grader.interface.js';
import type { EvaluationTemplate } from '../../../types/evaluationTemplate.js';

import {
  addBindingFlowError,
  analyzeBindingFlow,
  applyGraderModelFallback,
  classifyBindingFlowDiagnostics,
  collectBindingVariables,
  variableHasDefault,
} from './bindingFlow.utils.js';
import type { BindingFlowResource } from './bindingFlow.utils.js';
import { defaultBindingFor } from './bindings.utils.js';
import { AgentEditor } from './components/AgentEditor/AgentEditor.js';
import { AgentTypeField } from './components/AgentTypeField/AgentTypeField.js';
import { BindingFlowPanel } from './components/BindingFlowPanel/BindingFlowPanel.js';
import { ConcurrencyCard } from './components/ConcurrencyCard/ConcurrencyCard.js';
import { EvalModeCard } from './components/EvalModeCard/EvalModeCard.js';
import { FormFooter } from './components/FormFooter/FormFooter.js';
import { GradersList } from './components/GradersList/GradersList.js';
import { JobIdentityCard } from './components/JobIdentityCard/JobIdentityCard.js';
import { QueryDataSetCards } from './components/QueryDataSetCards/QueryDataSetCards.js';
import { seedFormStateFromTemplate } from './EvaluationTemplateForm.seed.js';
import { useStyles } from './EvaluationTemplateForm.style.js';
import type { EvaluationTemplateWithConfig } from './EvaluationTemplateForm.types.js';
import { parseInlineItems } from './EvaluationTemplateForm.utils.js';
import { useBindingRows } from './hooks/useBindingRows.js';
import { useEvaluationTemplateCatalogs } from './hooks/useEvaluationTemplateCatalogs.js';
import { useFileSource } from './hooks/useFileSource.js';
import { useFormFields } from './hooks/useFormFields.js';
import { useSubmitTemplate } from './hooks/useSubmitTemplate.js';
import { useTaskDrafts } from './hooks/useTaskDrafts.js';

export type { EvaluationTemplateWithConfig };

export interface EvaluationTemplateFormOutletContext {
  onCreated: (created: EvaluationTemplate) => void;
}

/**
 * Edit-mode Save state reported up to the detail view so the Save action can
 * live in the title bar next to Edit (mirrors the Agent form's controls).
 */
export interface TemplateEditControls {
  canSave: boolean;
  saving: boolean;
  save: () => void;
}

export interface EvaluationTemplateFormProps {
  mode?: 'create' | 'view' | 'edit';
  initialTemplate?: EvaluationTemplateWithConfig;
  onCreated?: (created: EvaluationTemplate) => void;
  onEditSaved?: (updated: EvaluationTemplate) => void;
  /** Reports edit Save state up while in edit mode (null when unmounted). */
  onEditControlsChange?: (controls: TemplateEditControls | null) => void;
}

export function EvaluationTemplateForm({
  mode = 'create',
  initialTemplate,
  onCreated,
  onEditSaved,
  onEditControlsChange,
}: EvaluationTemplateFormProps = {}) {
  const viewOnly = mode === 'view';
  const editing = mode === 'edit';
  const outletContext =
    useOutletContext<EvaluationTemplateFormOutletContext | null>();
  const handleCreated = onCreated ?? outletContext?.onCreated;
  const readOnly = viewOnly || editing;
  const metadataDisabled = viewOnly;
  const seeded = useMemo(
    () => (initialTemplate ? seedFormStateFromTemplate(initialTemplate) : null),
    [initialTemplate],
  );
  const styles = useStyles();
  const { activeWorkspace, isLoading: workspaceLoading } = useWorkspaces();
  const pinnedAgentVersions = useMemo(
    () =>
      Object.fromEntries(
        (seeded?.taskDrafts ?? [])
          .filter((task) => task.agent.version !== undefined)
          .map((task) => [task.agent.name, task.agent.version!]),
      ),
    [seeded],
  );
  const pinnedGraderVersions = useMemo(
    () =>
      Object.fromEntries(
        (seeded?.taskDrafts ?? []).flatMap((task) =>
          task.graderRows
            .filter((grader) => grader.version !== undefined)
            .map((grader) => [grader.name, grader.version!]),
        ),
      ),
    [seeded],
  );

  // ── Form fields (scalars + variables list) ────────────────────────────
  const {
    jobName,
    setJobName,
    jobDisplayName,
    setJobDisplayName,
    jobDescription,
    setJobDescription,
    querySetMode,
    setQuerySetMode,
    queryInlineText,
    setQueryInlineText,
    dataSetEnabled,
    setDataSetEnabled,
    dataSetMode,
    setDataSetMode,
    dataInlineText,
    setDataInlineText,
    dataSetExistingId,
    setDataSetExistingId,
    dataSetExistingVersion,
    setDataSetExistingVersion,
    dataSetFileSelector,
    setDataSetFileSelector,
    agentType,
    setAgentType,
    workersText,
    setWorkersText,
    parallelism,
    setParallelism,
    variables,
    addVariable,
    updateVariable,
    removeVariable,
  } = useFormFields(seeded);

  // ── File-mode pickers (query + data set) ──────────────────────────────
  const queryFile = useFileSource();
  const dataFile = useFileSource();

  // ── Task (single-run: one task, one agent reference) ──────────────────
  const { taskDrafts, updateAgent, updateTask, addGraderRow, removeGraderRow } =
    useTaskDrafts({ initialDrafts: seeded?.taskDrafts });
  const selectedAgentNames = useMemo(
    () =>
      taskDrafts
        .map((task) => task.agent.name)
        .filter((name) => name.length > 0),
    [taskDrafts],
  );
  const selectedGraderNames = useMemo(
    () =>
      taskDrafts.flatMap((task) =>
        task.graderRows
          .map((grader) => grader.name)
          .filter((name) => name.length > 0),
      ),
    [taskDrafts],
  );

  // ── Catalogs (graders + agents) ───────────────────────────────────────
  const { gradersLoading, uniqueGraders, agents, agentsLoading, catalogError } =
    useEvaluationTemplateCatalogs({
      viewOnly,
      activeWorkspaceName: activeWorkspace?.name,
      pinnedAgentVersions,
      pinnedGraderVersions,
      selectedAgentNames,
      selectedGraderNames,
    });

  // Only single-run is supported.
  const evalMode = 'single-run' as const;
  const primaryTask = taskDrafts[0]!;
  // Evaluation templates reference agents and graders unpinned: the submitted
  // body omits the version so each run resolves to the latest snapshot at run
  // time, letting a template pick up new snapshots without being re-edited.
  // Strip any version carried over from an older, pinned template so re-saving
  // it migrates the reference to unpinned.
  const submissionTaskDrafts = useMemo(
    () =>
      taskDrafts.map((task) => ({
        ...task,
        agent: { ...task.agent, version: undefined },
        graderRows: task.graderRows.map((graderRow) => ({
          ...graderRow,
          version: undefined,
        })),
      })),
    [taskDrafts],
  );

  const querySetItems = useMemo<string[] | null>(() => {
    if (querySetMode === 'inline') return parseInlineItems(queryInlineText);
    if (querySetMode === 'file') return queryFile.items;
    return null;
  }, [querySetMode, queryInlineText, queryFile.items]);

  const dataSetItems = useMemo<string[] | null>(() => {
    if (!dataSetEnabled) return null;
    if (dataSetMode === 'inline') return parseInlineItems(dataInlineText);
    if (dataSetMode === 'file') return dataFile.items;
    return null;
  }, [dataSetEnabled, dataSetMode, dataInlineText, dataFile.items]);

  const selectedResources = useMemo<BindingFlowResource[]>(() => {
    const resources: BindingFlowResource[] = [];
    const selectedAgent = agents.find(
      (agent) => agent.name === primaryTask.agent.name,
    );
    if (selectedAgent !== undefined) {
      resources.push({
        kind: 'Agent',
        name: selectedAgent.name,
        version: selectedAgent.version,
        spec: selectedAgent.spec,
        variables: selectedAgent.variables,
      });
    }
    for (const graderRow of primaryTask.graderRows) {
      const grader = uniqueGraders.find(
        (candidate) => candidate.name === graderRow.name,
      );
      if (grader !== undefined) {
        resources.push({
          kind: 'Grader',
          name: grader.name,
          version: grader.version,
          spec: grader.spec,
          variables: grader.variables,
        });
      }
    }
    return resources;
  }, [agents, primaryTask.agent.name, primaryTask.graderRows, uniqueGraders]);
  const {
    resources: bindingResources,
    shouldDeclare: shouldSupplyGraderModel,
  } = useMemo(
    () => applyGraderModelFallback(selectedResources, variables),
    [selectedResources, variables],
  );

  const bindingVariables = useMemo(
    () => collectBindingVariables(variables, bindingResources),
    [bindingResources, variables],
  );
  const getDefaultBinding = useCallback(
    (name: string) =>
      variableHasDefault(name, variables, bindingResources)
        ? ''
        : defaultBindingFor(name),
    [bindingResources, variables],
  );

  // Bindings cover variables declared or referenced by the selected resources,
  // not only variables declared directly on the template.
  const { bindingRows, setBinding } = useBindingRows(bindingVariables, {
    getDefault: getDefaultBinding,
    initialOverrides: seeded?.bindings,
  });

  const bindingFlow = useMemo(() => {
    let analysis = analyzeBindingFlow({
      resources: bindingResources,
      templateVariables: variables,
      bindings: bindingRows,
      queryItems: querySetItems,
      dataItems: dataSetItems,
      dataSetEnabled,
      dataSetInspectable: dataSetMode !== 'existing',
    });
    if (catalogError !== null && catalogError !== undefined) {
      analysis = addBindingFlowError(
        analysis,
        'catalog-resolution',
        `Unable to resolve selected component snapshots: ${catalogError}`,
      );
    }
    if (!agentsLoading && !gradersLoading && catalogError == null) {
      if (
        primaryTask.agent.name.length > 0 &&
        !agents.some((agent) => agent.name === primaryTask.agent.name)
      ) {
        analysis = addBindingFlowError(
          analysis,
          'missing-agent',
          `Agent ${primaryTask.agent.name} could not be resolved in this workspace.`,
        );
      }
      for (const graderRow of primaryTask.graderRows) {
        if (
          graderRow.name.length > 0 &&
          !uniqueGraders.some((grader) => grader.name === graderRow.name)
        ) {
          analysis = addBindingFlowError(
            analysis,
            `missing-grader-${graderRow.name}`,
            `Grader ${graderRow.name} could not be resolved in this workspace.`,
          );
        }
      }
    }
    return analysis;
  }, [
    agents,
    agentsLoading,
    bindingResources,
    bindingRows,
    dataSetEnabled,
    dataSetItems,
    dataSetMode,
    querySetItems,
    variables,
    catalogError,
    gradersLoading,
    primaryTask.agent.name,
    primaryTask.graderRows,
    uniqueGraders,
  ]);
  const bindingDiagnostics = useMemo(
    () => classifyBindingFlowDiagnostics(bindingFlow),
    [bindingFlow],
  );

  const {
    canSubmit,
    submitting,
    submitError,
    editSaving,
    editError,
    handleSubmit,
    handleEditSave,
    handleCancel,
  } = useSubmitTemplate({
    jobName,
    jobDisplayName,
    jobDescription,
    taskDrafts: submissionTaskDrafts,
    variables,
    additionalVariables: shouldSupplyGraderModel
      ? {
          [GRADER_MODEL_VARIABLE]: {
            type: 'string',
            default: DEFAULT_GRADER_MODEL,
          },
        }
      : undefined,
    bindingRows,
    bindingFlowHasErrors:
      bindingFlow.hasErrors || agentsLoading || gradersLoading,
    querySetMode,
    querySetItems,
    dataSetEnabled,
    dataSetMode,
    dataSetItems,
    dataSetExistingId,
    dataSetExistingVersion,
    dataSetFileSelector,
    workersText,
    parallelism,
    workspaceLoading,
    activeWorkspaceName: activeWorkspace?.name,
    initialTemplate,
    onCreated: handleCreated,
    onEditSaved,
  });

  // Report Save state up while editing so the detail view's title bar can own
  // the Save action (placed alongside Edit). Mirrors AgentEditControls.
  useEffect(() => {
    if (!editing) return;
    onEditControlsChange?.({
      canSave: canSubmit,
      saving: editSaving,
      save: () => void handleEditSave(),
    });
  }, [editing, onEditControlsChange, canSubmit, editSaving, handleEditSave]);
  useEffect(() => () => onEditControlsChange?.(null), [onEditControlsChange]);

  if (!readOnly && workspaceLoading) {
    return (
      <div className={styles.root}>
        <Spinner label="Loading workspace..." />
      </div>
    );
  }
  if (!readOnly && activeWorkspace === null) {
    return (
      <div className={styles.root}>
        <Body1>
          Pick a workspace from the top bar before creating an eval template.
        </Body1>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.scroll}>
        {!readOnly && activeWorkspace !== null && (
          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <span>{activeWorkspace.displayName ?? activeWorkspace.name}</span>
            <span>›</span>
            <a
              href={`/workspaces/${encodeURIComponent(activeWorkspace.name)}/evaluation-templates`}
              className={styles.breadcrumbLink}
              onClick={(e) => {
                e.preventDefault();
                handleCancel();
              }}
            >
              Eval templates
            </a>
            <span>›</span>
            <span className={styles.breadcrumbCurrent}>New eval</span>
          </nav>
        )}

        {!readOnly && (
          <div className={styles.header}>
            <Title2>New eval template</Title2>
            <Body1 className={styles.subtitle}>
              Author a reusable, versioned configuration for an evaluation.
            </Body1>
          </div>
        )}

        <div className={styles.cards}>
          <JobIdentityCard
            mode={mode}
            metadataDisabled={metadataDisabled}
            evalMode={evalMode}
            jobName={jobName}
            onJobNameChange={setJobName}
            jobDisplayName={jobDisplayName}
            onJobDisplayNameChange={setJobDisplayName}
            jobDescription={jobDescription}
            onJobDescriptionChange={setJobDescription}
            taskName={primaryTask.name}
            onTaskNameChange={(s) => updateTask(primaryTask.id, { name: s })}
          />

          <fieldset disabled={viewOnly} className={styles.fieldsetReset}>
            {/* Only single-run is selectable; the other modes are shown
                disabled, so onChange is a no-op. */}
            <EvalModeCard
              evalMode={evalMode}
              locked={editing}
              onChange={() => undefined}
            />

            <QueryDataSetCards
              viewOnly={viewOnly}
              querySetMode={querySetMode}
              onQuerySetModeChange={setQuerySetMode}
              queryInlineText={queryInlineText}
              onQueryInlineTextChange={setQueryInlineText}
              queryFileName={queryFile.name}
              queryFileItemCount={queryFile.items?.length ?? null}
              queryFileAnalysis={queryFile.analysis}
              queryFileError={queryFile.error}
              queryErrors={bindingDiagnostics.querySet}
              onQueryFile={queryFile.handle}
              dataSetEnabled={dataSetEnabled}
              onDataSetEnabledChange={setDataSetEnabled}
              dataSetMode={dataSetMode}
              onDataSetModeChange={setDataSetMode}
              dataInlineText={dataInlineText}
              onDataInlineTextChange={setDataInlineText}
              dataFileName={dataFile.name}
              dataFileItemCount={dataFile.items?.length ?? null}
              dataFileAnalysis={dataFile.analysis}
              dataFileError={dataFile.error}
              dataErrors={bindingDiagnostics.dataSet}
              onDataFile={dataFile.handle}
              dataSetExistingId={dataSetExistingId}
              dataSetExistingVersion={dataSetExistingVersion}
              onDataSetExistingChange={(id, version) => {
                // Switching datasets invalidates any auto-detected file
                // selector — clear it so the format picker re-detects for
                // the new selection rather than persisting a stale glob.
                if (id !== dataSetExistingId) setDataSetFileSelector([]);
                setDataSetExistingId(id);
                setDataSetExistingVersion(version);
              }}
              dataSetFileSelector={dataSetFileSelector}
              onDataSetFileSelectorChange={setDataSetFileSelector}
            />

            <section className={styles.card} aria-labelledby="card-agent">
              <div id="card-agent" className={styles.cardLabel}>
                Agent
              </div>
              <div className={styles.cardBody}>
                <AgentTypeField
                  agentType={agentType}
                  locked={editing}
                  onChange={setAgentType}
                />
                <AgentEditor
                  agent={primaryTask.agent}
                  agents={agents}
                  agentsLoading={agentsLoading}
                  onUpdate={(patch) =>
                    updateAgent(primaryTask.id, primaryTask.agent.id, patch)
                  }
                />
              </div>
            </section>

            <section className={styles.card} aria-labelledby="card-graders">
              <div id="card-graders" className={styles.cardLabel}>
                Graders
              </div>
              <GradersList
                task={primaryTask}
                uniqueGraders={uniqueGraders}
                gradersLoading={gradersLoading}
                viewOnly={viewOnly}
                onAddGraderRow={(name) => addGraderRow(primaryTask.id, name)}
                onRemoveGraderRow={(rowId) =>
                  removeGraderRow(primaryTask.id, rowId)
                }
              />
            </section>

            <BindingFlowPanel
              analysis={bindingFlow}
              bindings={bindingRows}
              onBindingChange={setBinding}
              onVariableUpdate={updateVariable}
              onVariableAdd={addVariable}
              onVariableRemove={removeVariable}
              viewOnly={viewOnly}
            />

            <ConcurrencyCard
              workersText={workersText}
              onWorkersChange={setWorkersText}
              parallelism={parallelism}
              onParallelismChange={setParallelism}
            />

            {!readOnly && submitError !== null && (
              <div className={styles.errorText} role="alert">
                {submitError}
              </div>
            )}
            {editing && editError !== null && (
              <div className={styles.errorText} role="alert">
                {editError}
              </div>
            )}
          </fieldset>
        </div>

        {mode === 'create' && (
          <FormFooter
            mode="create"
            canSubmit={canSubmit}
            submitting={submitting}
            onSubmit={() => void handleSubmit()}
            onCancel={handleCancel}
          />
        )}
      </div>
    </div>
  );
}
